// dependency-graph-ignore-file

import { parse, type ParseConfig, type ParseStepResult } from 'papaparse';

import { FilterLang } from './ParsingLangs/FilterLang.ts';
import { HiddenFieldsLang } from './ParsingLangs/HiddenFieldsLang.ts';
import { LayoutLang } from './ParsingLangs/LayoutLang.ts';
import { SortLang } from './ParsingLangs/SortLang.ts';
import { parseConfiguration } from './configuration-parsers/parse-configuration.ts';
import {
  CsvErrorCategory,
  CsvErrorCollector,
  CsvErrorType
} from './csv-error-collector.ts';
import { createParsingMachine, isAnOutsideState } from './state-machine/state-machine.ts';
import { parseEditMetadataRow } from './table-parsers/edit-metadata.ts';
import {
  type AddFilterToken,
  type AddHiddenFieldToken,
  type FieldToken,
  FILTERED_VALUE_KEYS,
  type LayoutToken,
  type OnTokenHandler,
  type ParsedRow,
  PARSER_STATES,
  type SortToken,
  type DataValueStrategy,
  type StreamingCsvParserOptions,
  type Token,
  TokenType,
  type ValueToken,
  type ViewToken
} from './types.ts';

/**
 * This class takes the raw output from the LLM and emits tokens
 */
export class StreamingCsvParser {
  onToken: OnTokenHandler;
  buffer = '';
  state: PARSER_STATES = PARSER_STATES.INIT;
  headerLine: string | null = null;
  handleStep: (row: ParsedRow) => void;
  parsingConfig: ParseConfig;
  operationType: 'create' | 'edit' = 'create';
  errorCollector: CsvErrorCollector;
  private stateMachine: ReturnType<typeof createParsingMachine>;
  private readonly dataValueStrategy: DataValueStrategy;

  constructor(
    onEmitToken: OnTokenHandler,
    operationType: 'create' | 'edit',
    options: StreamingCsvParserOptions = {}
  ) {
    this.onToken = onEmitToken;
    this.handleStep = this.processRow.bind(this);
    this.errorCollector = new CsvErrorCollector();
    this.dataValueStrategy = options.dataValueStrategy ?? { kind: 'positional' };
    this.parsingConfig = {
      header: true,
      dynamicTyping: false,
      skipEmptyLines: false,
      step: (results: ParseStepResult<ParsedRow>) => {
        this.handleStep(results.data);
      }
    };
    this.operationType = operationType;
    this.stateMachine = createParsingMachine();
  }

  static fromCompleteCSV(
    csvContent: string,
    operationType: 'create' | 'edit',
    options?: StreamingCsvParserOptions
  ): Token[] {
    const tokens: Token[] = [];
    const parser = new StreamingCsvParser(
      (token) => tokens.push(token),
      operationType,
      options
    );

    parser.onReceiveChunk(csvContent + '\n');

    return tokens;
  }

  private createSortTokens(row: ParsedRow): SortToken[] {
    if (!row['sorts']) {
      return [];
    }

    const viewName = row['view_name'];

    try {
      const tokens: Omit<SortToken, 'viewName'>[] = SortLang.Statement.tryParse(
        row['sorts']
      );

      return tokens.map((token) => ({
        ...token,
        viewName
      }));
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      this.errorCollector.addError(
        `Error creating sort tokens: ${errorMessage}`,
        CsvErrorCategory.SORT,
        CsvErrorType.PARSING,
        `view_name: ${viewName}, sorts: ${row['sorts']}`
      );
      console.error('Error creating sort tokens', error);
      return [];
    }
  }

  private createFilterTokens(row: ParsedRow): AddFilterToken[] {
    if (!row['filters']) {
      return [];
    }

    const viewName = row['view_name'];

    try {
      const tokens: Omit<AddFilterToken, 'viewName'>[] = [
        FilterLang.Statement.tryParse(row['filters'])
      ];

      return tokens.map((token) => ({
        ...token,
        viewName
      }));
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      this.errorCollector.addError(
        `Error creating filter tokens: ${errorMessage}`,
        CsvErrorCategory.FILTER,
        CsvErrorType.PARSING,
        `view_name: ${viewName}, filters: ${row['filters']}`
      );
      console.error('Error creating filter tokens', error);
      return [];
    }
  }

  private mapFieldsOperation(row: ParsedRow): Token[] {
    return [this.createFieldToken(row)];
  }

  private mapViewsOperation(row: ParsedRow): Token[] {
    const tokens: Token[] = [this.createViewToken(row)];

    const layoutToken = this.createLayoutToken(row);
    if (layoutToken) {
      tokens.push(layoutToken);
    }

    const sortTokens = this.createSortTokens(row);
    for (const sortToken of sortTokens) {
      tokens.push(sortToken);
    }

    const filterTokens = this.createFilterTokens(row);
    for (const filterToken of filterTokens) {
      tokens.push(filterToken);
    }

    const hiddenFieldsTokens = this.createHiddenFieldsTokens(row);
    for (const hiddenFieldsToken of hiddenFieldsTokens) {
      tokens.push(hiddenFieldsToken);
    }

    return tokens;
  }

  private mapEntriesOperation(row: ParsedRow): Token[] {
    const tokens: Token[] = [];

    try {
      tokens.push({
        tokenType: TokenType.ADD_ENTRY
      });

      const valueTokens = this.createValueTokens(row);
      for (const valueToken of valueTokens) {
        tokens.push(valueToken);
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      this.errorCollector.addError(
        `Error mapping entries operation: ${errorMessage}`,
        CsvErrorCategory.ENTRY,
        CsvErrorType.PARSING,
        `row: ${JSON.stringify(row)}`
      );
      console.error('Error mapping entries operation', error);
    }

    return tokens;
  }

  private mapDataOperation(row: ParsedRow): Token[] {
    const tokens: Token[] = [];

    try {
      const operationType = row['operation_type'];

      switch (operationType) {
        case 'add_entry':
          tokens.push({
            tokenType: TokenType.ADD_ENTRY,
            rowNumber: row['entry_index']
          });
          break;
        case 'edit_entry':
          tokens.push({
            tokenType: TokenType.EDIT_ENTRY,
            rowNumber: row['entry_index']
          });
          break;
        case 'delete_entry':
          tokens.push({
            tokenType: TokenType.REMOVE_ENTRY,
            rowNumber: row['entry_index']
          });
          return tokens;
        default:
          if (operationType) {
            this.errorCollector.addError(
              `Unknown operation_type: ${operationType}`,
              CsvErrorCategory.ENTRY,
              CsvErrorType.PARSING,
              `row: ${JSON.stringify(row)}`
            );
            console.error(`Unknown operation_type: ${operationType}`);
          }
      }

      const valueTokens = this.createValueTokens(row, true);
      for (const valueToken of valueTokens) {
        tokens.push(valueToken);
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      this.errorCollector.addError(
        `Error mapping data operation: ${errorMessage}`,
        CsvErrorCategory.ENTRY,
        CsvErrorType.PARSING,
        `row: ${JSON.stringify(row)}`
      );
      console.error('Error mapping data operation', error);
    }

    return tokens;
  }

  processRow(row: ParsedRow) {
    let tokens: Token[] = [];

    try {
      if (this.stateMachine.state() === 'create_fields') {
        tokens = this.mapFieldsOperation(row);
      } else if (this.stateMachine.state() === 'create_views') {
        tokens = this.mapViewsOperation(row);
      } else if (this.stateMachine.state() === 'create_data') {
        tokens = this.mapEntriesOperation(row);
      } else if (this.stateMachine.state() === 'edit_meta') {
        tokens = [...parseEditMetadataRow(row, this.errorCollector)];
      } else if (this.stateMachine.state() === 'edit_data') {
        tokens = this.mapDataOperation(row);
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      this.errorCollector.addError(
        `Error processing row: ${errorMessage}`,
        CsvErrorCategory.PARSING,
        CsvErrorType.PARSING,
        `state: ${this.stateMachine.state()}, row: ${JSON.stringify(row)}`
      );
      console.error('Error processing row', error);
      return;
    }

    for (const token of tokens) {
      this.onToken(token);
    }
  }

  private createValueTokens(row: ParsedRow, resetValue = false): ValueToken[] {
    switch (this.dataValueStrategy.kind) {
      case 'positional':
        return this.createPositionalValueTokens(row, resetValue);
      case 'single-target':
        return this.createSingleTargetValueTokens(
          row,
          this.dataValueStrategy.targetFieldName,
          resetValue
        );
      default: {
        const unsupportedStrategy: never = this.dataValueStrategy;
        throw new Error(
          `Unsupported data value strategy: ${String(unsupportedStrategy)}`
        );
      }
    }
  }

  // NOTE: This is a temporary fix to support the single-target data value
  // strategy due to hallucination or misplaced values. Eventually, we will
  // update the response shape of the teammate API to emit single summaries that
  // correspond to a single target field name.
  private createSingleTargetValueTokens(
    row: ParsedRow,
    targetFieldName: string,
    resetValue: boolean
  ): ValueToken[] {
    const nonEmptyValues = Object.entries(row).flatMap(([key, value]) => {
      if (FILTERED_VALUE_KEYS.includes(key as (typeof FILTERED_VALUE_KEYS)[number])) {
        return [];
      }

      if (key === '__parsed_extra') {
        return Array.isArray(value)
          ? value.filter((extraValue) => extraValue !== '')
          : [];
      }

      return value === '' ? [] : [value];
    });

    if (nonEmptyValues.length !== 1) {
      return [];
    }

    return [
      {
        tokenType: TokenType.VALUE,
        fieldName: targetFieldName,
        value: nonEmptyValues[0],
        resetValue
      }
    ];
  }

  private createPositionalValueTokens(row: ParsedRow, resetValue = false): ValueToken[] {
    const valueTokens: ValueToken[] = [];
    const rowKeys = new Set(Object.keys(row));

    try {
      for (const [key, value] of Object.entries(row)) {
        if (FILTERED_VALUE_KEYS.includes(key as any)) {
          continue;
        }

        if (this.isDuplicateColumnRenamed(key, rowKeys)) {
          continue;
        }

        try {
          valueTokens.push({
            tokenType: TokenType.VALUE,
            fieldName: key,
            value,
            resetValue
          });
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : String(error);
          this.errorCollector.addError(
            `Error creating value token for field '${key}': ${errorMessage}`,
            CsvErrorCategory.VALUE,
            CsvErrorType.PARSING,
            `fieldName: ${key}, value: ${value}, resetValue: ${resetValue}`
          );
          console.error(`Error creating value token for field '${key}'`, error);
        }
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      this.errorCollector.addError(
        `Error creating value tokens: ${errorMessage}`,
        CsvErrorCategory.VALUE,
        CsvErrorType.PARSING,
        `row: ${JSON.stringify(row)}`
      );
      console.error('Error creating value tokens', error);
    }

    return valueTokens;
  }

  /**
   * Detects if a column key was renamed by PapaParse due to duplicate headers.
   * PapaParse automatically renames duplicate column headers by appending `_N`
   * (e.g., "Name" becomes "Name_1" for the second occurrence).
   * We skip these renamed columns since the duplicate field won't exist.
   */
  private isDuplicateColumnRenamed(key: string, rowKeys: Set<string>): boolean {
    const match = key.match(/^(.+)_(\d+)$/);
    if (!match) {
      return false;
    }
    const baseName = match[1];
    return rowKeys.has(baseName);
  }

  private createLayoutToken(row: ParsedRow): LayoutToken | undefined {
    if (!row['layout']) {
      return;
    }

    const viewName = row['view_name'];

    try {
      const token: Omit<LayoutToken, 'viewName'> = LayoutLang.Statement.tryParse(
        row['layout']
      );

      return {
        ...token, // the array will only be one element
        viewName
      };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      this.errorCollector.addError(
        `Error creating layout tokens: ${errorMessage}`,
        CsvErrorCategory.LAYOUT,
        CsvErrorType.PARSING,
        `view_name: ${viewName}, layout: ${row['layout']}`
      );
      console.error('Error creating layout tokens', error);
      return undefined;
    }
  }

  private createViewToken(row: ParsedRow): ViewToken {
    try {
      const viewName = row['view_name'];

      if (!viewName || typeof viewName !== 'string') {
        const errorMessage = `Invalid or missing view_name: ${viewName}`;
        this.errorCollector.addError(
          errorMessage,
          CsvErrorCategory.VIEW,
          CsvErrorType.PARSING,
          `row: ${JSON.stringify(row)}`
        );
        console.error(errorMessage);
      }

      const viewToken: ViewToken = {
        tokenType: TokenType.VIEW,
        name: viewName
      };
      return viewToken;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      this.errorCollector.addError(
        `Error creating view token: ${errorMessage}`,
        CsvErrorCategory.VIEW,
        CsvErrorType.PARSING,
        `row: ${JSON.stringify(row)}`
      );
      console.error('Error creating view token', error);
      throw error;
    }
  }

  private createHiddenFieldsTokens(row: ParsedRow): AddHiddenFieldToken[] {
    if (!row['hidden_fields']) {
      return [];
    }

    try {
      const fieldNames: string[] = HiddenFieldsLang.Statement.tryParse(
        row['hidden_fields']
      );
      if (fieldNames.length === 0) {
        return [];
      }
      return [
        {
          tokenType: TokenType.ADD_HIDDEN_FIELD,
          viewName: row['view_name'],
          fieldNames
        }
      ];
    } catch (error) {
      console.error('Error creating hidden fields tokens', error);
      return [];
    }
  }

  private createFieldToken(row: ParsedRow): FieldToken {
    try {
      const fieldName = row['field_name'];
      const fieldType = row['type'];

      if (!fieldName || typeof fieldName !== 'string') {
        const errorMessage = `Invalid or missing field_name: ${fieldName}`;
        this.errorCollector.addError(
          errorMessage,
          CsvErrorCategory.FIELD,
          CsvErrorType.PARSING,
          `row: ${JSON.stringify(row)}`
        );
        console.error(errorMessage);
      }

      if (!fieldType) {
        const errorMessage = `Missing field type for field: ${fieldName}`;
        this.errorCollector.addError(
          errorMessage,
          CsvErrorCategory.FIELD,
          CsvErrorType.PARSING,
          `field_name: ${fieldName}, row: ${JSON.stringify(row)}`
        );
        console.error(errorMessage);
      }

      const token: FieldToken = {
        tokenType: TokenType.FIELD,
        name: fieldName,
        type: fieldType
      };

      if (row.configuration) {
        const parsedConfig = parseConfiguration(row['configuration']);
        if (parsedConfig) {
          token.configuration = parsedConfig;
        }
      }

      return token;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      this.errorCollector.addError(
        `Error creating field token: ${errorMessage}`,
        CsvErrorCategory.FIELD,
        CsvErrorType.PARSING,
        `row: ${JSON.stringify(row)}`
      );
      console.error('Error creating field token', error);
      throw error;
    }
  }

  public static isXMLOpeningTag(line: string): boolean {
    return /^<[^/][^>]+>$/.test(line);
  }

  public static isXMLTag(line: string, tagName: string): boolean {
    return new RegExp(`^<${tagName}>$`).test(line);
  }

  public static isXMLClosingTag(line: string): boolean {
    return /^<\/[^>]+>$/.test(line);
  }

  onReceiveChunk(chunk: string) {
    this.buffer += chunk;

    const lines = this.buffer.split('\n');
    this.buffer = lines.pop() || '';

    for (const line of lines) {
      if (StreamingCsvParser.isXMLTag(line, 'fields')) {
        this.stateMachine.send({ type: 'enteringFieldsTag' });
      } else if (StreamingCsvParser.isXMLTag(line, 'metadata')) {
        this.stateMachine.send({ type: 'enteringMetaTag' });
      }

      if (StreamingCsvParser.isXMLOpeningTag(line)) {
        this.stateMachine.send({ type: 'openingBracket' });
        this.headerLine = null;
        continue;
      }

      if (StreamingCsvParser.isXMLClosingTag(line)) {
        this.stateMachine.send({ type: 'closingBracket' });
        continue;
      }

      if (isAnOutsideState(this.stateMachine.state())) {
        // do not consume lines not wrapped in XML
        continue;
      }

      let isCurrentLineTheHeader = !this.headerLine;

      if (isCurrentLineTheHeader) {
        const expectedHeaderLine = this.getExpectedHeaderLine();
        if (expectedHeaderLine && !this.isValidHeaderLine(line, expectedHeaderLine)) {
          isCurrentLineTheHeader = false;
          this.headerLine = expectedHeaderLine;
        } else {
          this.headerLine = line;
          continue;
        }
      }

      if (!isCurrentLineTheHeader) {
        const stringToParse = [...(this.headerLine ? [this.headerLine] : []), line].join(
          '\n'
        );
        parse(stringToParse, this.parsingConfig);
      }
    }
  }

  private isValidHeaderLine(line: string, expectedHeaderLine: string): boolean {
    if (line === expectedHeaderLine) {
      return true;
    }

    if (this.stateMachine.state() === 'create_fields') {
      const normalizedLine = line.replace(/,+$/, '');
      return normalizedLine === 'field_name,type,configuration';
    }

    if (this.stateMachine.state() === 'create_views') {
      const normalizedLine = line.replace(/,+$/, '');
      return normalizedLine === 'view_name,layout,filters,sorts,hidden_fields';
    }

    return false;
  }

  private getExpectedHeaderLine(): string | undefined {
    switch (this.stateMachine.state()) {
      case 'create_fields':
        return 'field_name,type,configuration';
      case 'create_views':
        return 'view_name,layout,filters,sorts,hidden_fields';
      case 'edit_meta':
        return 'operation_type,target,property,value';
    }
  }
}
